<template>
  <div>
    <v-card class="mt-6">
      <v-card-title>
        <v-col cols="12" sm="1">
          <router-link
            :to="{ name: 'WillSummary', params: { id: userTestatorId } }"
            class="back-arrow"
            ><v-icon large color="mid-grey">{{
              icons.mdiArrowLeft
            }}</v-icon></router-link
          >
        </v-col>
        <v-col cols="12" sm="6">
          <h2>Audit trail for {{ userName }}</h2>
        </v-col>
        <v-col sm="5" cols="12">
          <div class="d-flex">
            <v-text-field
              v-model.trim="searchText"
              label="Search testator name or email"
              single-line
              hide-details
              clearable
              @click:clear="clearSearch()"
              @keydown.enter.prevent="searchData()"
              @blur="searchData()"
            />
            <v-btn depressed color="grey" @click="searchData()">
              <v-icon>{{ icons.mdiMagnify }}</v-icon>
              Search
            </v-btn>
          </div>
        </v-col>
      </v-card-title>
      <v-data-table
        class="admin-list-table"
        :headers="$options.headers"
        :items="versions"
        :loading="loading || $apollo.loading"
        :options.sync="options"
        :server-items-length="totalVersions"
        :footer-props="{
          itemsPerPageOptions: [10, 25, 100],
        }"
      >
        <template #[`item.createdAt`]="{ item }">
          {{ item.createdAt | time }}
        </template>

        <template #[`item.testatorName`]="{ item }">
          {{ item.testatorName }}<br />
          {{ item.testatorEmail }}
        </template>

        <template #[`item.objectChanges`]="{ item }">
          <span v-html="formattedObjectChanges(item)" />
        </template>

        <template #[`item.event`]="{ item }">
          {{ formattedEvent(item.event) }} {{ item.itemType }}
        </template>

        <template #[`item.extraDetail`]="{ item }">
          <span v-html="formattedExtraDetail(item)" />
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
  import {
    AUDIT_TRAIL_QUERY,
    AUDIT_TRAIL_USER_QUERY,
  } from '../../constants/graphql/users';
  import yaml from 'js-yaml';

  export default {
    name: 'AuditTrail',
    data() {
      return {
        totalVersions: null,
        versions: [],
        user: {},
        loading: true,
        options: {},
        searchText: '',
        searchQueryParam: '',
      };
    },
    headers: [
      { text: 'Changed at', value: 'createdAt' },
      { text: 'Testator', value: 'testatorName', sortable: false },
      { text: 'Event', value: 'event', sortable: false },
      { text: 'Object changes', value: 'objectChanges', sortable: false },
      {
        text: 'Extra context from related data',
        value: 'extraDetail',
        sortable: false,
      },
    ],
    computed: {
      userName() {
        return this.user.fullName ? this.user.fullName : this.user.email;
      },
      userTestatorId() {
        return this.user.testator ? this.user.testator.id : 0;
      },
    },
    watch: {
      options: {
        handler() {
          this.loading = true;
        },
        deep: true,
      },
    },
    methods: {
      queryVariables() {
        const limit = this.options.itemsPerPage || 10;
        const page = this.options.page || 1;
        let sortBy = null;
        let sortDesc = true;
        const search = this.searchQueryParam;
        const userId = this.$route.params.id;

        if (this.options.sortBy && this.options.sortBy.length) {
          sortBy = this.options.sortBy[0];
          sortDesc = this.options.sortDesc[0];
        }

        return { userId, page, limit, search, sortBy, sortDesc };
      },
      searchData() {
        this.searchQueryParam = this.searchText;
        this.options.page = 1;
      },
      clearSearch() {
        this.searchQueryParam = null;
        this.options.page = 1;
      },
      listChanges(objectChanges) {
        const changes = yaml.load(objectChanges);
        var html = '';
        for (const [key, value] of Object.entries(changes)) {
          if (key != 'updated_at' && key != 'created_at') {
            html += `<b>${key}</b> changed from <b>${value[0]}</b> to <b>${value[1]}</b> <br>`;
          }
        }

        return html;
      },
      formattedObjectChanges(item) {
        const changes = yaml.load(item.objectChanges);
        var html = '';

        if (item.event == 'destroy') {
          for (const [key, value] of Object.entries(changes)) {
            if (!this.excludeKey(key) && value[0]) {
              html += `${key}: <b>${value[0]}</b><br>`;
            }
          }
        } else if (item.event == 'create') {
          for (const [key, value] of Object.entries(changes)) {
            if (!this.excludeKey(key)) {
              html += `${key}: <b>${value[1]}</b><br>`;
            }
          }
        } else if (item.event == 'update') {
          for (const [key, value] of Object.entries(changes)) {
            if (!this.excludeKey(key)) {
              html += `${key}: <b>${value[0]}</b> -> <b>${value[1]}</b><br>`;
            }
          }
        } else {
          return this.listChanges(item.objectChanges);
        }

        return html;
      },
      excludeKey(key) {
        return [
          'updated_at',
          'created_at',
          'id',
          'details',
          'testator_id',
          'remainder_of_estate_id',
          'encrypted_number_iv',
        ].includes(key);
      },
      formattedEvent(event) {
        switch (event) {
          case 'create':
            return 'Created';
          case 'destroy':
            return 'Deleted';
          case 'update':
            return 'Updated';
          default:
            return event;
        }
      },
      formattedExtraDetail(item) {
        var html = '';
        if (item.extraDetail) {
          for (const [key, value] of Object.entries(item.extraDetail)) {
            html += `${key}: <b>${value}</b><br>`;
          }
        }

        return html;
      },
    },
    apollo: {
      versions: {
        query: AUDIT_TRAIL_QUERY,
        variables() {
          return this.queryVariables();
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          if (response.data) {
            this.totalVersions = response.data.auditTrail.totalVersions;
            this.loading = false;
          }
        },
        update: data => data.auditTrail.versions,
      },
      user: {
        query: AUDIT_TRAIL_USER_QUERY,
        variables() {
          return {
            id: this.$route.params.id,
          };
        },
      },
    },
  };
</script>
